import React, { useState } from 'react'

import {
  ComparativePeriod,
  ForecastPeriod,
  InnerWidgetPatchBody,
  PeriodFilter,
  TWidgetSettings,
  WidgetClass
} from 'types/GlobalWidget'

import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Switch from 'components_new/atoms/Switch'

import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import { TInputType } from 'components_new/molecules/SettingsEditDialog/utils'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import { useTranslation } from 'language/translator'

import {
  ALLOW_COMPARATIVE,
  getComparativePeriodOptions,
  getDateFilterOptions,
  getDisabledComparative,
  getDisabledForecast,
  getDisabledRestrictToPeriod,
  getForecastPeriodOptions
} from './utils'
import { Option } from 'components_new/molecules/SelectSearch'

interface DatesSectionProps {
  widgetClass: WidgetClass
  onClose: () => void
  settings: TWidgetSettings
  updateWidget: (body: InnerWidgetPatchBody) => void
}

const DatesSection = (props: DatesSectionProps) => {
  const { widgetClass, onClose, settings, updateWidget } = props
  const { translateEnum, t } = useTranslation()

  const handleToggle = (
    property:
      | 'date_filter_is_locked'
      | 'restrict_to_period'
      | 'show_date'
      | 'use_start_of_period_filter'
      | 'use_like_for_like_real_estates'
  ) => {
    let body: InnerWidgetPatchBody = {}

    switch (property) {
    case 'date_filter_is_locked':
      body = {
        date_filter_is_locked: !settings.date_filter_is_locked
      }
      break
    case 'restrict_to_period':
      body = {
        restrict_to_period: !settings.date_filter.restrict_to_period.selected
      }
      break
    case 'show_date':
      body = {
        show_date: !settings.show_date
      }
      break
    case 'use_start_of_period_filter':
      body = {
        use_start_of_period_filter:
            !settings.date_filter.use_start_of_period_filter
      }
      break
    case 'use_like_for_like_real_estates':
      body = {
        use_like_for_like_real_estates:
            !settings.date_filter.use_like_for_like_real_estates
      }
      break
    }

    updateWidget(body)
  }

  /*-- drawer --*/
  const [drawer, setDrawer] = useState<{
    open: boolean
    title: string
  }>({
    open: false,
    title: ''
  })

  const handleBackDrawer = () => {
    setDrawer((prevState) => ({ ...prevState, open: false }))
  }

  const handleCloseDrawer = () => {
    handleBackDrawer()
    onClose()
  }

  const handleOpenDrawer = () => {
    setDrawer({
      open: true,
      title: t('global', 'advanced')
    })
  }

  /*-- dialogs --*/
  const [dialog, setDialog] = useState<{
    open: boolean
    property: TProperty
    title: string
    data: {
      options: Option[]
      value: string
      type: TInputType
    }
  }>({
    open: false,
    property: 'comparative_period',
    title: '',
    data: {
      options: [],
      value: '',
      type: 'text'
    }
  })

  // Properties edited in this component.
  type TProperty = 'comparative_period' | 'date_filter' | 'forecast_period'

  const getPropertyTitle = (property: TProperty) => {
    switch (property) {
    case 'comparative_period':
      return t('global', 'compare_with')
    case 'date_filter':
      return t('global', 'historical')
    case 'forecast_period':
      return t('global', 'forecast')
    }
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = (property: TProperty) => {
    let inputValue = ''
    let inputType: TInputType = 'text'
    let inputOptions: Option[] = []

    switch (property) {
    case 'comparative_period':
      inputValue = settings.comparative_period.selected.value ?? 'none'
      inputType = 'select'
      inputOptions = getComparativePeriodOptions(
        settings.comparative_period.options,
        settings.date_filter.selected.value,
        settings.date_filter.type,
        t,
        translateEnum
      )
      break
    case 'date_filter':
      inputValue = settings.date_filter.selected.value
      inputType = 'select'
      inputOptions = getDateFilterOptions(
        settings.date_filter.options,
        translateEnum
      )
      break
    case 'forecast_period':
      inputValue = settings.forecast_period.selected ?? 'none'
      inputType = 'select'
      inputOptions = getForecastPeriodOptions(t, translateEnum)
      break
    }

    setDialog({
      open: true,
      property: property,
      title: getPropertyTitle(property),
      data: {
        options: inputOptions,
        type: inputType,
        value: inputValue
      }
    })
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    if (true) {
      let body: InnerWidgetPatchBody = {}

      switch (dialog.property) {
      case 'comparative_period':
        body = {
          comparative_period:
              dialog.data.value === 'none' || !dialog.data.value
                ? null
                : (dialog.data.value as ComparativePeriod)
        }
        break
      case 'date_filter':
        body = {
          date_filter: dialog.data.value as PeriodFilter
        }
        break
      case 'forecast_period':
        body = {
          forecast_period:
              dialog.data.value === 'none'
                ? null
                : (dialog.data.value as ForecastPeriod)
        }
        break
      }

      updateWidget(body)
    }
  }

  /*-- disabled --*/
  const { disabledComparative, disabledComparativeText } =
    getDisabledComparative(settings, t)
  const { disabledForecast, disabledForecastText } = getDisabledForecast(
    settings,
    t,
    translateEnum
  )

  return (
    <>
      <SettingsGroup title={t('date_filters', 'time_period')}>
        {widgetClass === WidgetClass.DASHBOARD ? (
          <SettingsItem
            actions={
              <Switch
                checked={settings.show_date}
                onChange={() => handleToggle('show_date')}
                size="small"
              />
            }
            title={t('global', 'show')}
          />
        ) : null}
        <>
          <SettingsItem
            icon={<Icon name={'HistoryOutlined'} />}
            onClick={() => handleOpenDialog('date_filter')}
            title={t('global', 'historical')}
            value={
              settings.date_filter.selected?.value
                ? translateEnum(
                  'period_filters',
                  settings.date_filter.selected.value
                ).title
                : null
            }
            variant="edit"
          />
          {settings.forecast_period.available ? (
            <SettingsItem
              disabled={disabledForecast}
              disabledText={disabledForecastText}
              icon={<Icon name={'UpdateOutlined'} />}
              onClick={() => handleOpenDialog('forecast_period')}
              title={t('global', 'forecast')}
              value={
                settings.forecast_period.selected
                  ? translateEnum(
                    'forecast_periods',
                    settings.forecast_period.selected
                  ).title
                  : null
              }
              variant="edit"
            />
          ) : null}
          {ALLOW_COMPARATIVE.includes(settings.type.selected) &&
          widgetClass !== WidgetClass.EXPORT_TABLE ? (
                <SettingsItem
                  disabled={disabledComparative}
                  disabledText={disabledComparativeText}
                  icon={<Icon name={'ImportExportOutlined'} />}
                  onClick={() => handleOpenDialog('comparative_period')}
                  title={t('global', 'compare_with')}
                  value={
                    settings.comparative_period.selected.value &&
                settings.date_filter.type &&
                settings.date_filter.selected?.value
                      ? translateEnum(
                        'comparative_periods',
                        settings.date_filter.type
                      )[settings.date_filter.selected.value][
                        settings.comparative_period.selected.value
                      ]
                      : null
                  }
                  variant="edit"
                />
              ) : null}
        </>
        {settings.date_filter.advanced_available ? (
          <SettingsItem
            onClick={handleOpenDrawer}
            title={t('global', 'advanced')}
          />
        ) : null}
      </SettingsGroup>

      {/*-- drawer --*/}
      <SettingsDrawer
        open={drawer.open}
        onBack={handleBackDrawer}
        onClose={handleCloseDrawer}
        title={drawer.title}
      >
        <SettingsGroup
          helperText={t('widgets', 'lock_date_filter_helper_text')}
        >
          <SettingsItem
            actions={
              <Switch
                checked={settings.date_filter_is_locked}
                onChange={() => handleToggle('date_filter_is_locked')}
                size="small"
              />
            }
            title={t('global', 'lock')}
          />
        </SettingsGroup>

        <SettingsGroup
          helperText={t(
            'widgets',
            'use_like_for_like_real_estates_helper_text'
          )}
        >
          <SettingsItem
            actions={
              <Switch
                checked={settings.date_filter.use_like_for_like_real_estates}
                onChange={() => handleToggle('use_like_for_like_real_estates')}
                size="small"
              />
            }
            title={t('widgets', 'use_like_for_like_real_estates')}
          />
        </SettingsGroup>

        <SettingsGroup
          helperText={t('widgets', 'use_start_of_period_helper_text')}
        >
          <SettingsItem
            actions={
              <Switch
                checked={settings.date_filter.use_start_of_period_filter}
                onChange={() => handleToggle('use_start_of_period_filter')}
                size="small"
              />
            }
            title={t('widgets', 'use_start_of_period')}
          />
        </SettingsGroup>

        <SettingsGroup
          helperText={
            <>
              {t('widgets', 'restrict_to_closed_period_helper_text')}{' '}
              <Link newTab to={'/admin/settings/config'} underline="always">
                {t('global', 'read_more_on_homepal_se')}
              </Link>
            </>
          }
        >
          <SettingsItem
            actions={
              <Switch
                checked={settings.date_filter.restrict_to_period.selected}
                onChange={() => handleToggle('restrict_to_period')}
                size="small"
              />
            }
            disabled={!settings.date_filter.restrict_to_period.available}
            disabledText={getDisabledRestrictToPeriod(
              settings,
              t,
              translateEnum
            )}
            title={t('widgets', 'latest_closed_financial_period')}
          />
        </SettingsGroup>
      </SettingsDrawer>

      {/*-- dialogs --*/}
      <SettingsEditDialog
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={dialog.data.options}
        title={dialog.title}
        type={dialog.data.type}
        value={dialog.data.value}
      />
    </>
  )
}

export default DatesSection
